/*
 * -----------------------------------------------------------------------
 *  <copyright file="heat-map-chart.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import 'highcharts/highcharts-more';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/esm/modules/dumbbell.js';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import HighchartsReact from 'highcharts-react-official';
import React, { useState, useEffect, useRef, useContext } from 'react';

import { HC_COLORS_STANDARD, IOpraChartKey, OpraColours, OpraVerticalAlign } from 'interfaces/opra-chart.interface';
import Loading from 'views/components/core/loading';
import { PanelFrameContext } from 'views/components/core/panel-frame';

export interface IOpraDumbbellPlot {
    label: string;
    averageValue: number;
    instanceValue: number;
}

export interface IOpraDumbbellSeries {
    name: string;
    color: string;
    lowColor?: string;
    plots: IOpraDumbbellPlot[];
    tooltip?: {
        headerFormat?: string;
        pointFormat?: string;
        footerFormat?: string;
    };
}

export interface IOpraDumbbellChart {
    title: string;
    xAxisTitle?: string;
    yAxisTitle?: string;
    series: IOpraDumbbellSeries[];
    getRef?: Function;
    secondaryKey?: IOpraChartKey;
    showLegend?: boolean;
    hidePlotTitle?: boolean;
}

const DumbbellChart = (chartProps: IOpraDumbbellChart) => {
    const { title, xAxisTitle, yAxisTitle, getRef, secondaryKey, showLegend, hidePlotTitle } = chartProps;
    const [options, setOptions] = useState<HighchartsReact.Props | null>(null);
    const charts = useRef<HighchartsReact.RefObject>(null);
    const height = useContext(PanelFrameContext);
    const [style, setStyle] = useState({
        style: {
            height: '350px',
        },
    });

    useEffect(() => {
        if (height?.height && height?.height !== style.style.height)
            setStyle({
                style: {
                    height: height?.height,
                },
            });
    }, [height?.height]);

    useEffect(() => {
        if (getRef && charts) {
            getRef(charts?.current);
            setTimeout(() => {
                if (charts.current) charts.current.chart.reflow();
            }, 0);
        }
    }, [charts.current, options]);

    useEffect(() => {
        setOptions({
            chart: {
                type: 'dumbbell',
                inverted: true,
                zoomType: 'xy',
            },
            exporting: {
                chartOptions: { title: { text: title, style: { fontWeight: 'semi-bold' } } },
                enabled: !getRef,
                fallbackToExportServer: false,
                buttons: {
                    contextButton: {
                        enabled: false,
                    },
                },
            },
            legend: {
                enabled: false,
                itemStyle: {
                    color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                    cursor: 'pointer',
                    fontWeight: 'normal',
                    fontSize: '12px',
                    textOverflow: 'ellipsis',
                },
            },
            title: {
                text: hidePlotTitle ? null : title,
            },
            subtitle:
                secondaryKey === undefined
                    ? undefined
                    : {
                          text: secondaryKey.text,
                          useHTML: true,
                          verticalAlign:
                              secondaryKey.position === OpraVerticalAlign.top
                                  ? 'top'
                                  : secondaryKey.position === OpraVerticalAlign.middle
                                  ? 'middle'
                                  : 'bottom',
                          style: { display: showLegend ?? true ? undefined : 'none' },
                      },
            tooltip: {
                shared: true,
            },
            xAxis: {
                type: 'category',
                title: {
                    text: yAxisTitle,
                },
            },
            yAxis: {
                min: 0,
                title: {
                    text: xAxisTitle,
                },
            },
            plotOptions: {
                dumbbell: {
                    connectorColor: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                    lowColor: HC_COLORS_STANDARD[OpraColours.DarkBlue],
                    marker: {
                        symbol: 'circle',
                    },
                },
            },
            series:
                chartProps?.series?.map((s: IOpraDumbbellSeries) => ({
                    name: s.name,
                    marker: {
                        fillColor: s.color,
                    },
                    color: s.color,
                    tooltip: s.tooltip,
                    data: s.plots.map((p) => [p.label, p.averageValue, p.instanceValue]),
                })) ?? [],
        });
    }, [chartProps]);

    if (options === null) return <Loading />;

    return (
        <HighchartsReact
            ref={charts}
            highcharts={Highcharts}
            constructorType="chart"
            options={options}
            containerProps={style} // Mending size after fullscreen and setting with for expand chart functionality
        />
    );
};

export default DumbbellChart;
